import { useEffect, useMemo, useState } from 'react';
import ResolvedAddress from '../../components/ResolvedAddress';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import AppLayout from '../../components/AppLayout';
import { listBookings } from '../../services/bookingService';
import { getCompanyProfile } from '../../services/companyService';
import { getFare } from '../../services/fareService';
import {
  buildModernReceiptPdf,
  buildTripFromBooking,
  formatAddressOrCoords,
  normalizeLatLon,
} from '../../utils/receiptPdf';

const defaultFilters = {
  driver: '',
  pickup: '',
  dropoff: '',
  from: '',
  to: '',
};

// encodePolyline kept locally — only needed to build Mapbox URLs for the interactive map modal
function encodePolyline(points) {
  let lastLat = 0;
  let lastLon = 0;
  let encoded = '';
  const encodeValue = (value) => {
    let coordinate = value < 0 ? ~(value << 1) : value << 1;
    let result = '';
    while (coordinate >= 0x20) {
      result += String.fromCharCode((0x20 | (coordinate & 0x1f)) + 63);
      coordinate >>= 5;
    }
    result += String.fromCharCode(coordinate + 63);
    return result;
  };
  for (const point of points) {
    const lat = Math.round(Number(point.lat) * 1e5);
    const lon = Math.round(Number(point.lon) * 1e5);
    encoded += encodeValue(lat - lastLat);
    encoded += encodeValue(lon - lastLon);
    lastLat = lat;
    lastLon = lon;
  }
  return encoded;
}

const ReceiptGenerator = () => {
  const [company, setCompany] = useState({
    name: 'TaxiOps Transportation LLC',
    address: '',
    phone: '',
    email: '',
  });
  const [trips, setTrips] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapModalCoords, setMapModalCoords] = useState({
    lat: null,
    lon: null,
    label: '',
    pickupLat: null,
    pickupLon: null,
    dropoffLat: null,
    dropoffLon: null,
    routeCoords: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(defaultFilters);

  useEffect(() => {
    let ignore = false;

    const unwrap = (response, preferredKeys = []) => {
      const payload = response?.data ?? response ?? {};
      if (Array.isArray(payload)) return payload;
      for (const key of preferredKeys) {
        const value = payload?.[key];
        if (Array.isArray(value)) return value;
      }
      if (Array.isArray(payload?.data)) return payload.data;
      const firstArray = Object.values(payload).find((value) => Array.isArray(value));
      return Array.isArray(firstArray) ? firstArray : [];
    };

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [companyRes, bookingsRes, fareRes] = await Promise.all([
          getCompanyProfile(),
          listBookings({ status: 'Completed', limit: 200, sort: 'desc' }),
          getFare().catch(() => ({ data: null })),
        ]);

        if (!ignore) {
          const fetchedCompany = companyRes.data?.company;
          if (fetchedCompany) {
            setCompany({
              name: fetchedCompany.name || 'TaxiOps Transportation LLC',
              address: fetchedCompany.address || '',
              phone: fetchedCompany.phone || '',
              email: fetchedCompany.email || '',
            });
          }

          const fareConfig = fareRes?.data?.fare || fareRes?.data || fareRes || null;

          const fetchedTrips = unwrap(bookingsRes, ['bookings', 'results'])
            .filter((booking) => booking.status === 'Completed')
            .map((booking) => buildTripFromBooking(booking, fareConfig))
            .sort((a, b) => {
              const aMs = a.pickup ? Date.parse(a.pickup) : 0;
              const bMs = b.pickup ? Date.parse(b.pickup) : 0;
              return bMs - aMs;
            });

          setTrips(fetchedTrips);
        }
      } catch (err) {
        if (!ignore) {
          const message = err.response?.data?.message || 'Unable to load receipt data.';
          setError(message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      ignore = true;
    };
  }, []);

  const filteredTrips = useMemo(() => {
    const driverQuery = filters.driver.trim().toLowerCase();
    const pickupQuery = filters.pickup.trim().toLowerCase();
    const dropoffQuery = filters.dropoff.trim().toLowerCase();
    const fromMs = filters.from ? Date.parse(filters.from) : null;
    const toMs = filters.to ? Date.parse(filters.to) : null;

    return trips.filter((trip) => {
      if (driverQuery) {
        const driverText = `${trip.driver || ''} ${trip.driverId || ''}`.toLowerCase();
        if (!driverText.includes(driverQuery)) return false;
      }

      if (pickupQuery) {
        const pickupText = String(trip.origin || '').toLowerCase();
        if (!pickupText.includes(pickupQuery)) return false;
      }

      if (dropoffQuery) {
        const dropoffText = String(trip.destination || '').toLowerCase();
        if (!dropoffText.includes(dropoffQuery)) return false;
      }

      const pickupMs = trip.pickup ? Date.parse(trip.pickup) : Number.NaN;
      if (fromMs && (Number.isNaN(pickupMs) || pickupMs < fromMs)) return false;
      if (toMs && (Number.isNaN(pickupMs) || pickupMs > toMs)) return false;

      return true;
    });
  }, [trips, filters]);

  // reset page to 1 whenever filters change
  useEffect(() => setPage(1), [filters]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredTrips.length / (pageSize || 1)));
  }, [filteredTrips.length, pageSize]);

  // clamp page when filteredTrips or pageSize change
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedTrips = useMemo(() => {
    const size = Number(pageSize) || 20;
    const start = (Number(page) - 1) * size;
    return filteredTrips.slice(start, start + size);
  }, [filteredTrips, page, pageSize]);

  const filtersActive = useMemo(
    () => Object.values(filters).some((value) => value && value.length),
    [filters],
  );

  const totalFare = useMemo(
    () => filteredTrips.reduce((sum, trip) => sum + Number(trip.fare || 0), 0),
    [filteredTrips],
  );

  const companyDescriptor = useMemo(() => {
    const segments = [];
    if (company.name) segments.push(company.name);
    if (company.address) segments.push(company.address);
    const contactLine = [company.phone, company.email].filter(Boolean).join(' / ');
    if (contactLine) segments.push(contactLine);
    return segments.join(' | ');
  }, [company]);

  const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');

  const openTripMapModal = (pickupLat, pickupLon, dropoffLat, dropoffLon, label, routeCoords = []) => {
    const pickupCoords = normalizeLatLon(pickupLat, pickupLon);
    const dropoffCoords = normalizeLatLon(dropoffLat, dropoffLon);
    const normalizedRoute = Array.isArray(routeCoords)
      ? routeCoords.map((point) => normalizeLatLon(point?.lat, point?.lon)).filter(Boolean)
      : [];

    const coords = {
      pickupLat: pickupCoords?.lat ?? null,
      pickupLon: pickupCoords?.lon ?? null,
      dropoffLat: dropoffCoords?.lat ?? null,
      dropoffLon: dropoffCoords?.lon ?? null,
      routeCoords: normalizedRoute,
      lat: null,
      lon: null,
      label: label || '',
    };

    if (pickupCoords && !dropoffCoords) {
      coords.lat = pickupCoords.lat;
      coords.lon = pickupCoords.lon;
      coords.label = coords.label || `Pickup: ${pickupCoords.lat.toFixed(6)}, ${pickupCoords.lon.toFixed(6)}`;
    } else if (!pickupCoords && dropoffCoords) {
      coords.lat = dropoffCoords.lat;
      coords.lon = dropoffCoords.lon;
      coords.label = coords.label || `Dropoff: ${dropoffCoords.lat.toFixed(6)}, ${dropoffCoords.lon.toFixed(6)}`;
    } else if (pickupCoords && dropoffCoords) {
      coords.label = coords.label || `Trip: ${label || ''}`;
    }

    setMapModalCoords(coords);
    setMapModalOpen(true);
  };

  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

  // Local copy for the interactive map modal (needs MAPBOX_TOKEN closure + local encodePolyline)
  const buildMapboxStaticUrl = ({ pickupLat, pickupLon, dropoffLat, dropoffLon, routeCoords = [], width = 900, height = 480 }) => {
    const token = (MAPBOX_TOKEN || '').trim();
    if (!token) return '';

    const mk = (id, lat, lon, color) => `pin-l-${id}+${color}(${lon},${lat})`;
    const normalizedRoute = Array.isArray(routeCoords)
      ? routeCoords.map((point) => normalizeLatLon(point?.lat, point?.lon)).filter(Boolean)
      : [];

    const overlays = [];
    if (normalizedRoute.length > 1) {
      overlays.push(`path-5+1d4ed8-0.9(${encodePolyline(normalizedRoute)})`);
    }
    if (pickupLat !== null && pickupLon !== null && dropoffLat !== null && dropoffLon !== null) {
      overlays.push(mk('a', pickupLat, pickupLon, '1e40af'), mk('b', dropoffLat, dropoffLon, 'dc2626'));
      return `https://api.mapbox.com/styles/v1/mapbox/navigation-day-v1/static/${encodeURIComponent(overlays.join(','))}/auto/${width}x${height}@2x?padding=60&access_token=${token}`;
    }

    const lat = pickupLat !== null && pickupLon !== null ? pickupLat : dropoffLat;
    const lon = pickupLat !== null && pickupLon !== null ? pickupLon : dropoffLon;
    if (lat === null || lon === null) return '';

    overlays.push(mk('pin', lat, lon, '1e40af'));
    return `https://api.mapbox.com/styles/v1/mapbox/navigation-day-v1/static/${encodeURIComponent(overlays.join(','))}/${lon},${lat},14/${width}x${height}@2x?access_token=${token}`;
  };

  const closeMapModal = () => {
    setMapModalOpen(false);
    setMapModalCoords({ lat: null, lon: null, label: '' });
  };

  const buildReceiptPdf = async (trip) => {
    await buildModernReceiptPdf(trip, company, MAPBOX_TOKEN);
  };

  const downloadReceipts = async (targetTrips) => {
    if (!Array.isArray(targetTrips) || !targetTrips.length) return;
    for (const trip of targetTrips) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await buildReceiptPdf(trip);
      } catch (e) {
        // ignore and continue to next
      }
    }
  };

  const exportFilteredToCsv = () => {
    if (!filteredTrips.length) return;
    const headers = [
      'Trip ID',
      'Driver',
      'Cab',
      'Customer',
      'Pickup',
      'Drop-off',
      'Origin',
      'Destination',
      'Fare',
    ];
    const rows = filteredTrips.map((trip) => [
      trip.id ?? '',
      trip.driver ?? '',
      trip.cabNumber ? `Cab ${trip.cabNumber}` : '',
      trip.customer ?? '',
      formatDateTime(trip.pickup),
      formatDateTime(trip.dropoff),
      formatAddressOrCoords(trip.origin, trip.pickupLat, trip.pickupLon),
      formatAddressOrCoords(trip.destination, trip.dropoffLat, trip.dropoffLon),
      Number(trip.fare || 0).toFixed(2),
    ]);
    const csvLines = [headers, ...rows].map((line) =>
      line
        .map((value) => {
          const safe = value === undefined || value === null ? '' : String(value);
          return `"${safe.replace(/"/g, '""')}"`;
        })
        .join(','),
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'receipts.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => setFilters({ ...defaultFilters });

  return (
    <AppLayout
      title="Generate receipt"
      subtitle="Review completed trips and export a printable receipt for riders or corporate accounts."
    >
      <div className="panel" style={{ maxWidth: '1040px', margin: '0 auto' }}>
        <div
          className="panel-header"
          style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}
        >
          <div>
            <h3>Receipt library</h3>
            <p className="panel-subtitle">
              {companyDescriptor || 'TaxiOps Transportation LLC'}
            </p>
          </div>
          <div className="panel-actions">
            <button
              type="button"
              className="btn btn-subtle"
              onClick={exportFilteredToCsv}
              disabled={loading || !!error || filteredTrips.length === 0}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => downloadReceipts(filteredTrips)}
              disabled={loading || !!error || filteredTrips.length === 0}
            >
              Download receipts (PDF)
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => downloadReceipts(paginatedTrips)}
              disabled={loading || !!error || paginatedTrips.length === 0}
            >
              Download page
            </button>
          </div>
        </div>

          <div className="panel-body" style={{ gap: '18px' }}>
          <div className="form-grid">
            <div>
              <label htmlFor="filterDriver">Driver</label>
              <input
                id="filterDriver"
                name="driver"
                type="text"
                value={filters.driver}
                onChange={handleFilterChange}
                placeholder="Name or ID"
              />
            </div>
            <div>
              <label htmlFor="filterPickup">Pickup address</label>
              <input
                id="filterPickup"
                name="pickup"
                type="text"
                value={filters.pickup}
                onChange={handleFilterChange}
                placeholder="Search pickup"
              />
            </div>
            <div>
              <label htmlFor="filterDropoff">Drop-off address</label>
              <input
                id="filterDropoff"
                name="dropoff"
                type="text"
                value={filters.dropoff}
                onChange={handleFilterChange}
                placeholder="Search drop-off"
              />
            </div>
            <div>
              <label htmlFor="filterFrom">From</label>
              <input
                id="filterFrom"
                name="from"
                type="datetime-local"
                value={filters.from}
                onChange={handleFilterChange}
              />
            </div>
            <div>
              <label htmlFor="filterTo">To</label>
              <input
                id="filterTo"
                name="to"
                type="datetime-local"
                value={filters.to}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div
            className="pill-group"
            style={{ justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}
          >
            <div>
              <span>
                Showing {filteredTrips.length} of {trips.length} trip
                {trips.length === 1 ? '' : 's'}.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ color: '#64748b' }}>Page size:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <button type="button" className="btn btn-ghost" onClick={resetFilters} disabled={!filtersActive}>
                Clear filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="skeleton" style={{ height: '240px', borderRadius: '16px' }} />
        ) : error ? (
          <div className="feedback error">{error}</div>
        ) : filteredTrips.length === 0 ? (
          <div className="fleet-alert-empty">No trips match the current filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Trip ID</th>
                  <th>Driver</th>
                  <th>Customer</th>
                  <th>Pickup</th>
                  <th>Drop-off</th>
                  <th>Origin</th>
                  <th>Destination</th>
                  <th style={{ textAlign: 'right' }}>Fare ($)</th>
                  <th style={{ textAlign: 'right' }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTrips.map((trip) => (
                  <tr key={trip.id}>
                    <td>{trip.id}</td>
                    <td>
                      <div className="table-stack">
                        <span className="primary">{trip.driver}</span>
                        <span className="secondary">
                          {[trip.driverId, trip.cabNumber && `Cab ${trip.cabNumber}`]
                            .filter(Boolean)
                            .join(' | ') || 'Unassigned'}
                        </span>
                      </div>
                    </td>
                    <td>{trip.customer}</td>
                    <td>{formatDateTime(trip.pickup)}</td>
                    <td>{formatDateTime(trip.dropoff)}</td>
                    <td><ResolvedAddress address={trip.origin} lat={trip.pickupLat} lon={trip.pickupLon} /></td>
                    <td><ResolvedAddress address={trip.destination} lat={trip.dropoffLat} lon={trip.dropoffLon} /></td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(trip.fare || 0).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={async () => {
                            try {
                              await buildReceiptPdf(trip);
                            } catch (e) {
                              // ignore
                            }
                          }}
                        >
                          PDF
                        </button>
                        <button
                          type="button"
                          className="btn btn-subtle"
                          onClick={() => openTripMapModal(trip.pickupLat, trip.pickupLon, trip.dropoffLat, trip.dropoffLon, `${trip.id}`, trip.routeCoords)}
                          disabled={!normalizeLatLon(trip.pickupLat, trip.pickupLon) && !normalizeLatLon(trip.dropoffLat, trip.dropoffLon)}
                        >
                          Map
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination controls */}
        {!loading && !error && filteredTrips.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setPage(1)} disabled={page <= 1}>
                ⏮ First
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                ◀ Prev
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                Next ▶
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
                Last ⏭
              </button>
            </div>

            <div style={{ color: '#64748b' }}>
              Page {page} of {totalPages} — {filteredTrips.length} result{filteredTrips.length === 1 ? '' : 's'}
            </div>
          </div>
        )}

        <div
          className="panel-footer"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <strong>Total collected:</strong> ${totalFare.toFixed(2)}
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
            Prepared on {new Date().toLocaleString()}
          </div>
        </div>
      </div>
        {/* Map preview modal */}
        {mapModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
            onClick={closeMapModal}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '90%',
                maxWidth: '860px',
                height: '70vh',
                background: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e6edf3', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontWeight: 600 }}>{mapModalCoords.label}</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {mapModalCoords.pickupLat !== null && mapModalCoords.pickupLon !== null && mapModalCoords.dropoffLat !== null && mapModalCoords.dropoffLon !== null ? (
                    <a
                      className="btn btn-ghost"
                      href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${mapModalCoords.pickupLat},${mapModalCoords.pickupLon}`)}&destination=${encodeURIComponent(`${mapModalCoords.dropoffLat},${mapModalCoords.dropoffLon}`)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Google Maps
                    </a>
                  ) : mapModalCoords.lat !== null && mapModalCoords.lon !== null ? (
                    <a
                      className="btn btn-ghost"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${mapModalCoords.lat},${mapModalCoords.lon}`)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Google Maps
                    </a>
                  ) : null}
                  <button type="button" className="btn btn-ghost" onClick={closeMapModal}>
                    Close
                  </button>
                </div>
              </div>
              {
                (normalizeLatLon(mapModalCoords.pickupLat, mapModalCoords.pickupLon) || normalizeLatLon(mapModalCoords.dropoffLat, mapModalCoords.dropoffLon) || mapModalCoords.routeCoords?.length > 1) ? (
                  (() => {
                    const pickupCoords = normalizeLatLon(mapModalCoords.pickupLat, mapModalCoords.pickupLon);
                    const dropoffCoords = normalizeLatLon(mapModalCoords.dropoffLat, mapModalCoords.dropoffLon);
                    const routePoints = Array.isArray(mapModalCoords.routeCoords)
                      ? mapModalCoords.routeCoords.map((point) => normalizeLatLon(point?.lat, point?.lon)).filter(Boolean)
                      : [];

                    function FitBounds({ points }) {
                      const map = useMap();
                      useEffect(() => {
                        if (!map || !points || !points.length) return;
                        try {
                          if (points.length === 1) {
                            map.setView(points[0], 15);
                          } else {
                            const latlngs = points.map((p) => [p.lat, p.lon]);
                            map.fitBounds(latlngs, { padding: [40, 40] });
                          }
                        } catch (e) {
                          // ignore
                        }
                      }, [map, points]);
                      return null;
                    }

                    const points = [];
                    if (pickupCoords) points.push({ lat: pickupCoords.lat, lon: pickupCoords.lon, label: 'Pickup' });
                    if (dropoffCoords) points.push({ lat: dropoffCoords.lat, lon: dropoffCoords.lon, label: 'Dropoff' });
                    const fitPoints = [...routePoints, ...points];
                    const routeLine = routePoints.map((point) => [point.lat, point.lon]);

                    const center = fitPoints.length ? { lat: fitPoints[0].lat, lon: fitPoints[0].lon } : { lat: 28.2919557, lon: -81.4075713 };

                    return (
                      <div style={{ width: '100%', height: '100%' }}>
                        <MapContainer center={[center.lat, center.lon]} zoom={13} style={{ width: '100%', height: '100%' }}>
                          {MAPBOX_TOKEN ? (
                            <TileLayer
                              attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                              url={`https://api.mapbox.com/styles/v1/mapbox/navigation-day-v1/tiles/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`}
                              tileSize={512}
                              zoomOffset={-1}
                            />
                          ) : (
                            <TileLayer
                              attribution='&copy; OpenStreetMap contributors'
                              url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                            />
                          )}
                          <FitBounds points={fitPoints} />
                          {routeLine.length > 1 ? (
                            <Polyline positions={routeLine} pathOptions={{ color: '#1d4ed8', weight: 5, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }} />
                          ) : null}
                          {points.map((p, idx) => (
                            <Marker key={idx} position={[p.lat, p.lon]}>
                              <Popup>{p.label}</Popup>
                            </Marker>
                          ))}
                        </MapContainer>
                      </div>
                    );
                  })()
                ) : (
                  (MAPBOX_TOKEN || '').trim() ? (
                    (() => {
                      const src = buildMapboxStaticUrl({
                        pickupLat: mapModalCoords.pickupLat,
                        pickupLon: mapModalCoords.pickupLon,
                        dropoffLat: mapModalCoords.dropoffLat,
                        dropoffLon: mapModalCoords.dropoffLon,
                        routeCoords: mapModalCoords.routeCoords,
                        width: 860,
                        height: 420,
                      });
                      return src ? (
                        <div style={{ width: '100%', height: '100%', background: '#f6fafc', display: 'flex', flexDirection: 'column' }}>
                          <img
                            alt={mapModalCoords.label || 'Map preview'}
                            src={src}
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#e9f2fb' }}
                          />
                          <div style={{ padding: '6px 10px', borderTop: '1px solid #eef3f8', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <a className="btn btn-ghost" href={src} target="_blank" rel="noreferrer">Open in Mapbox</a>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: 24 }}>No map preview available for this trip.</div>
                      );
                    })()
                  ) : (
                    <iframe
                      title="map-preview"
                      src={
                        mapModalCoords.pickupLat !== null && mapModalCoords.pickupLon !== null
                          ? `https://www.google.com/maps?q=${encodeURIComponent(`${mapModalCoords.pickupLat},${mapModalCoords.pickupLon}`)}&z=16&output=embed`
                          : mapModalCoords.dropoffLat !== null && mapModalCoords.dropoffLon !== null
                          ? `https://www.google.com/maps?q=${encodeURIComponent(`${mapModalCoords.dropoffLat},${mapModalCoords.dropoffLon}`)}&z=16&output=embed`
                          : mapModalCoords.lat !== null && mapModalCoords.lon !== null
                          ? `https://www.google.com/maps?q=${encodeURIComponent(`${mapModalCoords.lat},${mapModalCoords.lon}`)}&z=16&output=embed`
                          : ''
                      }
                      style={{ border: 0, width: '100%', height: '100%' }}
                      loading="lazy"
                    />
                  )
                )
              }
            </div>
          </div>
        )}
    </AppLayout>
  );
};

export default ReceiptGenerator;
